import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'
import { ArrowLeft, RefreshCw, AlertCircle, Wallet } from 'lucide-react'
import { api } from '../lib/api'

interface SchoolUserRow {
  userId: string | null
  displayName: string
  username: string | null
  endCustomerPriceEur: number
  llmEndCustomerPriceEur: number
  inputTokens: number
  outputTokens: number
  requestCount: number
  ttsCostEur?: number
  ttsChars?: number
  ttsRequestCount?: number
}

interface SchoolSummary {
  tenantId: string
  endCustomerPriceEur: number
  llmEndCustomerPriceEur: number
  inputTokens: number
  outputTokens: number
  requestCount: number
}

interface BillingAggregateRow {
  billingUnitId: string
  costEur: number
  endCustomerPriceEur?: number
  inputTokens: number
  outputTokens: number
  requestCount: number
}

interface AreaAggregateRow {
  key: string
  label: string
  endCustomerPriceEur: number
  requestCount: number
  inputTokens: number
  outputTokens: number
}

interface SchoolPayload {
  success: boolean
  hubConfigured: boolean
  tenantId: string
  period?: { from?: string; to?: string }
  summary: SchoolSummary | null
  areaAggregates?: AreaAggregateRow[]
  users: SchoolUserRow[]
  billingAggregates?: BillingAggregateRow[]
  billingNote?: string
  error?: string
}

const eur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' })
const num = new Intl.NumberFormat('de-DE')

function billingAggregateLabel(raw: string): string {
  const id = String(raw || '').trim()
  const cw = /^lr:classwork:(\d+)$/.exec(id)
  if (cw) return `Klassenarbeit #${cw[1]}`
  if (id === 'lr:email') return 'E-Mail / Korrespondenz (Assistenz)'
  if (id === 'lr:sonstige') return 'Weitere Assistenz-Nutzung'
  return id
}

export default function AdminHubKontostand() {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery<SchoolPayload>(
    'hub-usage-school',
    async () => {
      const res = await api.get('/hub/usage/school')
      return res.data
    },
    { staleTime: 60_000, retry: 1 }
  )

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-40 bg-gray-100 rounded-xl" />
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 shrink-0" />
          <div>
            <h1 className="text-lg font-semibold text-red-900">Fehler</h1>
            <p className="text-red-800 text-sm mt-1">
              {(error as { message?: string })?.message || 'Daten konnten nicht geladen werden.'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (!data?.hubConfigured) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Link to="/dashboard" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Zum Dashboard
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Wallet className="w-7 h-7 text-violet-600" />
          Rechnungsübersicht Assistenzsysteme
        </h1>
        <p className="mt-4 text-gray-600">
          Der Hub ist nicht konfiguriert. Setzen Sie <code className="text-sm bg-gray-100 px-1 rounded">HUB_BASE_URL</code>{' '}
          und optional <code className="text-sm bg-gray-100 px-1 rounded">HUB_API_KEY</code> in der Lernrakete-Backend-Umgebung.
        </p>
      </div>
    )
  }

  const s = data.summary
  const areaRows = Array.isArray(data.areaAggregates) ? data.areaAggregates : []
  const monthLabel =
    data.period?.from != null
      ? new Date(data.period.from).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })
      : 'aktueller Monat'

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <Link to="/dashboard" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-2">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Zum Dashboard
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Wallet className="w-7 h-7 text-violet-600" />
            Rechnungsübersicht Assistenzsysteme
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Mandant <span className="font-mono">{data.tenantId}</span> · Monatsverbrauch ({monthLabel}) ·{' '}
            Ihr Rechnungsbetrag
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-violet-700 bg-violet-50 rounded-lg hover:bg-violet-100 disabled:opacity-60"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          Aktualisieren
        </button>
      </div>

      {s && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-medium text-gray-500 uppercase">Ihr Rechnungsbetrag (gesamt)</p>
            <p className="text-2xl font-semibold text-gray-900 mt-1">{eur.format(s.endCustomerPriceEur)}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-medium text-gray-500 uppercase">Davon Text-Assistenz</p>
            <p className="text-2xl font-semibold text-gray-900 mt-1">{eur.format(s.llmEndCustomerPriceEur)}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-medium text-gray-500 uppercase">Anfragen (Text-Assistenz)</p>
            <p className="text-2xl font-semibold text-gray-900 mt-1">{num.format(s.requestCount || 0)}</p>
            <p className="text-xs text-gray-500 mt-2">aktueller Monat</p>
          </div>
          <div className="rounded-xl border border-violet-100 bg-violet-50/60 p-5 shadow-sm">
            <p className="text-xs font-medium text-violet-800 uppercase">Hinweis</p>
            <p className="text-sm text-violet-900 mt-2 leading-relaxed">
              Es gilt der Ihnen in Rechnung gestellte Betrag. Einkaufspreise bei Anbietern werden hier nicht ausgewiesen.
            </p>
          </div>
        </div>
      )}

      {areaRows.length > 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Monatsverbrauch nach Bereich</h2>
            <p className="text-sm text-gray-500 mt-1">Korrekturen, Analysen und E-Mail-Assistenz (Ihr Rechnungsbetrag).</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  <th className="px-4 py-3">Bereich</th>
                  <th className="px-4 py-3 text-right">Rechnungsbetrag</th>
                  <th className="px-4 py-3 text-right">Anfragen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {areaRows.map((row) => (
                  <tr key={row.key} className="hover:bg-gray-50/80">
                    <td className="px-4 py-3 font-medium text-gray-900">{row.label}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{eur.format(row.endCustomerPriceEur)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{num.format(row.requestCount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {Array.isArray(data.billingAggregates) && data.billingAggregates.length > 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Assistenz nach Vorgang</h2>
            <p className="text-sm text-gray-500 mt-1">
              {data.billingNote ||
                'Klassenarbeiten erscheinen als eine Abrechnungs-ID für alle Schüler. Detailnachweise zur Verwaltung stehen im Hub.'}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  <th className="px-4 py-3">Vorgang</th>
                  <th className="px-4 py-3 text-right">Rechnungsbetrag</th>
                  <th className="px-4 py-3 text-right">Anfragen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.billingAggregates.map((row) => (
                  <tr key={row.billingUnitId} className="hover:bg-gray-50/80">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{billingAggregateLabel(row.billingUnitId)}</div>
                      <div className="text-xs text-gray-500 font-mono mt-0.5">{row.billingUnitId}</div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {eur.format(row.endCustomerPriceEur ?? row.costEur)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{num.format(row.requestCount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Verbrauch pro Benutzer</h2>
          <p className="text-sm text-gray-500 mt-1">
            Übersicht nach Lehrkraft (technische Zuordnung). Für abrechnungsrelevante Summen pro Klassenarbeit siehe oben.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Login</th>
                <th className="px-4 py-3 text-right">Gesamt – Rechnungsbetrag</th>
                <th className="px-4 py-3 text-right">Text-Assistenz – Rechnungsbetrag</th>
                <th className="px-4 py-3 text-right">Anfragen (Text)</th>
                <th className="px-4 py-3 text-right">Sprachausgabe €</th>
                <th className="px-4 py-3 text-right">Sprachausgabe (Zeichen)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data.users || []).map((row, idx) => (
                <tr key={`${row.userId ?? 'anon'}-${idx}`} className="hover:bg-gray-50/80">
                  <td className="px-4 py-3 font-medium text-gray-900">{row.displayName}</td>
                  <td className="px-4 py-3 text-gray-600 font-mono text-xs">
                    {row.username ?? (row.userId ? `id:${row.userId}` : '—')}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{eur.format(row.endCustomerPriceEur)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{eur.format(row.llmEndCustomerPriceEur)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{num.format(row.requestCount)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {eur.format(row.ttsCostEur ?? 0)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-600">
                    {num.format(row.ttsChars ?? 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(!data.users || data.users.length === 0) && (
          <p className="px-6 py-8 text-center text-gray-500">Noch keine Nutzungsdaten im Hub für diesen Mandanten.</p>
        )}
      </div>
    </div>
  )
}
